import React, { useState, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import { useQuery } from "@apollo/client/react";
import useMutation from "../../../hooks/useMutation";
import useAuth from "../../../hooks/useAuth";
import useNotification from "../../../hooks/notification";
import { FAVORITE_REPORTS_QUERY } from "../../../constants/graphql/index";
import { UPDATE_FAVORITE_REPORT_MUTATION } from "../../../constants/graphql/mutations";

export default function AssignTemplateModal({ open, onClose, projectId }) {
  const { user } = useAuth();
  const { settings } = user.company;
  const { notifySuccess, notifyError } = useNotification();
  const [search, setSearch] = useState("");

  const { data: { favoriteReports = [] } = {}, loading } = useQuery(FAVORITE_REPORTS_QUERY, {
    variables: {},
    fetchPolicy: "cache-first",
  });

  const filteredReports = useMemo(() => {
    if (!search) return favoriteReports;
    return favoriteReports.filter(r =>
      r.payload?.name?.toLowerCase().includes(search.toLowerCase())
    );
  }, [favoriteReports, search]);

  const [updateFavoriteReport, { loading: assigning }] = useMutation(
    UPDATE_FAVORITE_REPORT_MUTATION,
    {
      onSuccess: () => {
        notifySuccess(`Template successfully assigned to ${settings.filter_field2}`);
        onClose();
      },
      onFailure: errors => {
        notifyError(errors);
      },
      refetchQueries: [{ query: FAVORITE_REPORTS_QUERY, variables: { projectId } }],
    }
  );

  const handleAssign = report => {
    const existingIds = (report.favoriteReportsProjects || [])
      .map(frp => frp.project?.id)
      .filter(Boolean);

    const updatedProjectIds = existingIds.includes(projectId)
      ? existingIds
      : [...existingIds, projectId];

    updateFavoriteReport({
      variables: {
        id: report.id,
        payload: report.payload,
        projectIds: updatedProjectIds,
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography fontSize={22} fontWeight={500} align="center" color="text.primary">
          Assign Template to {settings.filter_field2}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: "text.secondary",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 4, py: 3, flex: 1, display: "flex", flexDirection: "column" }}>
        <Box mb={3}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {loading ? (
          <Box py={4} display="flex" justifyContent="center" alignItems="center" flex={1}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Template Name</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReports.map(report => (
                  <TableRow key={report.id} hover>
                    <TableCell>{report.payload?.name}</TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleAssign(report)}
                        disabled={assigning}
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        Select Template
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredReports.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ textAlign: "center", py: 3 }}>
                      <Typography variant="body2" color="textSecondary">
                        No templates found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
}
